// 052 — Planificación Familiar (Fase 5, Tanda 5). Los bloques A-L oficiales
// están en `bloques052Datos.ts` (fuente: PDF páginas 180-181). El bloque A lo
// resuelve `EncabezadoMspCampos`, igual que 113/114/115/016.
//
// FHIR: un `Condition` por cada diagnóstico de J con texto (mismo criterio que
// 016) — todo lo demás (antecedentes ginecológicos, sexualidad, métodos,
// controles, examen, complementarios) queda como `Par[]` de respaldo en el PDF
// firmado, sin recursos nuevos sin consumidor demostrable.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloque, CamposSimples } from './camposSimples';
import { MARCAS_PRE_DEF, type CampoSimple, type Valores } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca, verificationStatusDeMarca } from './MarcaDiagnostico';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { TablaConColumnas } from './uci/TablaConColumnas';
import {
  CAMPOS_ADOPCION_METODO_052,
  CAMPOS_ATENCION_052,
  CAMPOS_BENEFICIARIO_052,
  CAMPOS_COMPLEMENTARIOS_052,
  CAMPOS_DETECCION_CANCER_052,
  CAMPOS_DOLOR_052,
  CAMPOS_EMBARAZO_METODO_052,
  CAMPOS_EXAMEN_FEMENINO_052,
  CAMPOS_EXAMEN_MASCULINO_052,
  CAMPOS_FECUNDIDAD_052,
  CAMPOS_HISTORIA_ANTICONCEPTIVA_052,
  CAMPOS_ITS_052,
  CAMPOS_PLANES_052,
  CAMPOS_PUBERAL_HOMBRE_052,
  CAMPOS_PUBERAL_MUJER_052,
  CAMPOS_RITMO_MENSTRUAL_052,
  CAMPOS_SEXUALIDAD_052,
  CAMPOS_USO_ACTUAL_052,
  COLUMNAS_CONTROLES_052,
  FILAS_CONTROLES_052,
  estadoInicialBitacora052,
  paresDeBitacora052,
  type EstadoBitacora052,
} from './bloques052Datos';

const CODIGO = '052';

type ClaveValores = { [K in keyof EstadoBitacora052]: EstadoBitacora052[K] extends Valores ? K : never }[keyof EstadoBitacora052];

export function Formulario052({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.052');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);

  const [estado, setEstado] = useState<EstadoBitacora052>(() => estadoInicialBitacora052());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = paresDeBitacora052(t, estado);
      const ahora = new Date().toISOString();
      const lote = new LoteTransaccional();
      for (const d of estado.diagnosticos) {
        if (d.diagnostico.texto.trim() !== '') {
          lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d.diagnostico), verificationStatus: verificationStatusDeMarca(d.marca), recordedDate: ahora });
        }
      }
      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  function bloque(clave: ClaveValores, tituloClave: string, prefijo: string, lista: readonly CampoSimple[], columnas?: 2 | 3 | 4): JSX.Element {
    return (
      <Bloque titulo={t(`formularios.f052.${tituloClave}`)}>
        <CamposSimples prefijoId={`f052-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} onChange={(v) => setEstado({ ...estado, [clave]: v })} />
      </Bloque>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <h4 className="border-b border-border pb-1 text-base font-medium text-hce-ink">{t('formularios.f052.atencionTitulo')}</h4>
      {bloque('atencion', 'atencionDatosTitulo', 'atencion', CAMPOS_ATENCION_052, 2)}
      {bloque('puberalMujer', 'puberalMujerTitulo', 'pm', CAMPOS_PUBERAL_MUJER_052, 4)}
      {bloque('puberalHombre', 'puberalHombreTitulo', 'ph', CAMPOS_PUBERAL_HOMBRE_052, 4)}
      {bloque('ritmoMenstrual', 'ritmoMenstrualTitulo', 'ritmo', CAMPOS_RITMO_MENSTRUAL_052, 4)}
      {bloque('dolor', 'dolorTitulo', 'dolor', CAMPOS_DOLOR_052)}
      {bloque('deteccionCancer', 'deteccionCancerTitulo', 'cancer', CAMPOS_DETECCION_CANCER_052, 4)}
      {bloque('sexualidad', 'sexualidadTitulo', 'sex', CAMPOS_SEXUALIDAD_052, 4)}

      <h4 className="border-b border-border pb-1 text-base font-medium text-hce-ink">{t('formularios.f052.metodosTitulo')}</h4>
      {bloque('embarazoMetodo', 'embarazoMetodoTitulo', 'emb', CAMPOS_EMBARAZO_METODO_052, 2)}
      {bloque('historiaAnticonceptiva', 'historiaAnticonceptivaTitulo', 'hist', CAMPOS_HISTORIA_ANTICONCEPTIVA_052, 3)}
      {bloque('usoActual', 'usoActualTitulo', 'uso', CAMPOS_USO_ACTUAL_052, 4)}
      {bloque('beneficiario', 'beneficiarioTitulo', 'ben', CAMPOS_BENEFICIARIO_052)}
      {bloque('adopcionMetodo', 'adopcionMetodoTitulo', 'adop', CAMPOS_ADOPCION_METODO_052, 3)}

      <Bloque titulo={t('formularios.f052.controlesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f052.controlesNota')}</p>
        <TablaConColumnas filas={FILAS_CONTROLES_052} columnas={COLUMNAS_CONTROLES_052} estado={estado.controles} onChange={(c) => setEstado({ ...estado, controles: c })} />
      </Bloque>

      {bloque('its', 'itsTitulo', 'its', CAMPOS_ITS_052, 4)}
      {bloque('fecundidad', 'fecundidadTitulo', 'fec', CAMPOS_FECUNDIDAD_052, 4)}
      {bloque('examenFemenino', 'examenFemeninoTitulo', 'ef', CAMPOS_EXAMEN_FEMENINO_052, 4)}
      {bloque('examenMasculino', 'examenMasculinoTitulo', 'em', CAMPOS_EXAMEN_MASCULINO_052, 4)}
      {bloque('complementarios', 'complementariosTitulo', 'comp', CAMPOS_COMPLEMENTARIOS_052, 4)}

      <Bloque titulo={t('formularios.f052.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f052.diagnosticoNota')}</p>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f052.diagnosticoTitulo')} ${i + 1}`}
            marcaLabel={t('formularios.f052.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      {bloque('planes', 'planesTitulo', 'planes', CAMPOS_PLANES_052, 2)}
      <Bloque titulo={t('formularios.f052.responsableTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
